import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Users2, FileText, Pill, AlertTriangle, HeartHandshake, Shield, Loader2, Eye, Download, ChevronRight,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import SharedFormattedMarkdown from "@/components/FormattedMarkdown";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BAND_META = {
    critical: { color: "text-[#9E2A2B]", bg: "bg-[#F9ECEC]", label: "Crítico" },
    high: { color: "text-[#C85A47]", bg: "bg-[#FBEEE6]", label: "Alto risco" },
    moderate: { color: "text-[#9C6E28]", bg: "bg-[#FBF4E5]", label: "Moderado" },
    low: { color: "text-[#556B2F]", bg: "bg-[#EDF3E8]", label: "Baixo risco" },
};

export default function CaregiverPortal() {
    const { user } = useAuth();
    const [patients, setPatients] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [tab, setTab] = useState("plan");
    const [carePlan, setCarePlan] = useState(null);
    const [monitoring, setMonitoring] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/portal/caregiver/patients");
                setPatients(data);
                if (data.length > 0) setSelectedId(data[0].id);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    useEffect(() => {
        if (!selectedId) return;
        setLoadingDetail(true);
        (async () => {
            try {
                const [cp, mon, rm] = await Promise.all([
                    api.get(`/portal/caregiver/patient/${selectedId}/care-plan`),
                    api.get(`/portal/caregiver/patient/${selectedId}/monitoring`),
                    api.get(`/portal/caregiver/patient/${selectedId}/reminders`),
                ]);
                setCarePlan(cp.data);
                setMonitoring(mon.data);
                setReminders(rm.data);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoadingDetail(false); }
        })();
    }, [selectedId]);

    if (loading) return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-brand" size={32} /></div></AppShell>;

    if (patients.length === 0) {
        return (
            <AppShell>
                <div className="card-earthy max-w-xl">
                    <HeartHandshake size={32} className="text-[var(--brand-secondary)] mb-3" />
                    <h1 className="font-display text-2xl font-medium text-brand mb-2">Você ainda não está vinculado a nenhum paciente</h1>
                    <p className="text-[var(--text-secondary)]">
                        Peça ao médico ou à equipe da clínica para vincular seu e-mail <strong>{user?.email}</strong> ao paciente que você cuida. Após a vinculação, você verá aqui o plano de cuidado, medicamentos e sinais de alerta.
                    </p>
                </div>
            </AppShell>
        );
    }

    const selected = patients.find((p) => p.id === selectedId);

    return (
        <AppShell>
            <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">Portal do cuidador</div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Olá, {user?.name?.split(" ")[0]}</h1>
                <p className="text-[var(--text-secondary)] mt-1">Acompanhe o cuidado de quem você cuida — dados protegidos pela LGPD.</p>
            </div>

            <div className="grid lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1 space-y-4">
                    <div className="card-earthy">
                        <div className="flex items-center gap-2 mb-3">
                            <Users2 size={18} className="text-brand" />
                            <h2 className="font-display text-sm font-medium text-brand uppercase tracking-wider">Pacientes</h2>
                        </div>
                        <div className="space-y-2">
                            {patients.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedId(p.id)}
                                    data-testid={`caregiver-portal-patient-${p.id}`}
                                    className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${selectedId === p.id ? "bg-brand text-white" : "bg-alt text-brand hover:bg-[#EAE4D8]"}`}
                                >
                                    <div className="font-medium">{p.full_name}</div>
                                    <div className={`text-xs mt-1 ${selectedId === p.id ? "opacity-75" : "text-muted-clinical"}`}>
                                        {p.birth_date || "—"} · {p.insurance || "—"}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {monitoring?.report && (
                        <div className={`card-earthy ${BAND_META[monitoring.report.risk_band]?.bg}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <AlertTriangle size={16} className={BAND_META[monitoring.report.risk_band]?.color} />
                                <span className={`text-xs font-semibold uppercase tracking-wider ${BAND_META[monitoring.report.risk_band]?.color}`}>Status atual</span>
                            </div>
                            <div className={`font-display text-lg font-medium ${BAND_META[monitoring.report.risk_band]?.color}`}>
                                {BAND_META[monitoring.report.risk_band]?.label || "—"}
                            </div>
                            <div className="text-xs text-muted-clinical mt-1">
                                {monitoring.report.findings?.length || 0} achados clínicos
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-3">
                    <div className="border-b border-[rgba(30,63,51,0.1)] mb-6 flex flex-wrap gap-1">
                        {[
                            { id: "plan", label: "Plano de cuidado", Icon: FileText },
                            { id: "monitoring", label: "Monitoramento", Icon: AlertTriangle },
                            { id: "reminders", label: "Medicamentos", Icon: Pill },
                            { id: "privacy", label: "Privacidade", Icon: Shield },
                        ].map((t) => {
                            const active = tab === t.id;
                            const N = t.Icon;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setTab(t.id)}
                                    data-testid={`caregiver-portal-tab-${t.id}`}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 -mb-px ${active ? "border-brand text-brand" : "border-transparent text-muted-clinical hover:text-brand"}`}
                                >
                                    <N size={14} /> {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {loadingDetail ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-brand" size={24} /></div>
                    ) : (
                        <>
                            {tab === "plan" && <PlanView plan={carePlan} patientName={selected?.full_name} />}
                            {tab === "monitoring" && <MonitoringView report={monitoring?.report} />}
                            {tab === "reminders" && <ReminderView list={reminders} />}
                            {tab === "privacy" && <PrivacyPanel />}
                        </>
                    )}
                </div>
            </div>
        </AppShell>
    );
}

function PlanView({ plan, patientName }) {
    if (!plan || !plan.patient_plan) {
        return <div className="card-earthy text-sm text-muted-clinical">Ainda não há plano de cuidado disponível para {patientName || "este paciente"}.</div>;
    }
    return (
        <div className="grid xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 card-earthy">
                <h2 className="font-display text-xl font-medium text-brand mb-3">Plano de cuidado — {patientName}</h2>
                <FormattedMarkdown text={plan.patient_plan} />
            </div>
            <div className="space-y-3">
                {plan.medications?.length > 0 && (
                    <div className="card-earthy">
                        <h3 className="font-display text-sm font-medium text-brand-secondary uppercase tracking-wider mb-3">Medicamentos prescritos</h3>
                        <ul className="text-sm space-y-2">
                            {plan.medications.map((m, i) => (
                                <li key={i} className="p-2 rounded-lg bg-alt">
                                    <div className="font-medium text-brand">{m.name}</div>
                                    <div className="text-xs text-muted-clinical">{m.dose} · {m.freq}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {plan.alerts?.length > 0 && (
                    <div className="card-earthy border-l-4 border-[var(--brand-accent)]">
                        <h3 className="font-display text-sm font-medium text-[var(--brand-accent)] uppercase tracking-wider mb-2">Quando procurar atendimento</h3>
                        <ul className="text-sm space-y-1">
                            {plan.alerts.map((a, i) => <li key={i} className="flex gap-2"><span className="text-[var(--brand-accent)]">•</span>{a}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

const SEV_CHIP = {
    high: "bg-[#9E2A2B] text-white",
    medium: "bg-[#D4A373] text-white",
    low: "bg-[#556B2F] text-white",
};

function MonitoringView({ report }) {
    if (!report) return <div className="card-earthy text-sm text-muted-clinical">Sem dados de monitoramento disponíveis.</div>;
    const meta = BAND_META[report.risk_band] || BAND_META.low;
    if (report.findings.length === 0) {
        return (
            <div className={`card-earthy ${meta.bg}`}>
                <div className={`text-sm font-medium ${meta.color}`}>{meta.label}</div>
                <p className="text-sm text-[var(--text-secondary)] mt-1">Nenhum sinal de alerta identificado. Continue com o cuidado atual.</p>
            </div>
        );
    }
    return (
        <div className="space-y-3">
            <div className={`card-earthy ${meta.bg} border-l-4 border-current ${meta.color}`}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-1">Status geral</div>
                <div className={`font-display text-2xl font-medium ${meta.color}`}>{meta.label}</div>
                <p className="text-sm text-[var(--text-secondary)] mt-1">Os itens abaixo foram identificados na última avaliação médica. Siga as orientações e informe a equipe se surgir alguma piora.</p>
            </div>
            {report.findings.map((f, i) => (
                <div key={i} className="card-earthy">
                    <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                            <div className="font-medium text-brand">{f.label}</div>
                            <div className="text-sm text-[var(--text-secondary)] mt-0.5">{f.detail}</div>
                        </div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${SEV_CHIP[f.severity]}`}>
                            {f.severity === "high" ? "Alto" : f.severity === "medium" ? "Médio" : "Baixo"}
                        </span>
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-secondary mb-1">O que fazer</div>
                    <ul className="text-sm space-y-1">
                        {f.recommendations.map((r, j) => (
                            <li key={j} className="flex items-start gap-2"><span className="text-[var(--brand-accent)] mt-1">•</span><span>{r}</span></li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function ReminderView({ list }) {
    if (list.length === 0) return <div className="card-earthy text-sm text-muted-clinical">Nenhum lembrete cadastrado.</div>;
    return (
        <div className="space-y-2">
            {list.map((r) => (
                <div key={r.id} className="card-earthy">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-alt flex items-center justify-center text-brand"><Pill size={16} /></div>
                        <div className="flex-1">
                            <div className="font-medium text-brand">{r.medication_name} <span className="text-sm text-muted-clinical font-normal">{r.dose}</span></div>
                            <div className="text-xs text-brand-secondary mt-1">{r.times_of_day.join(" · ")}</div>
                            {r.notes && <div className="text-xs text-[var(--text-secondary)] mt-1">{r.notes}</div>}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PrivacyPanel() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/portal/access-logs").then(({ data }) => { setLogs(data); setLoading(false); });
    }, []);
    const exportData = async () => {
        const { data } = await api.get("/portal/my-data-export");
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `dados_cuidador_${Date.now()}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Dados baixados");
    };
    return (
        <div className="grid lg:grid-cols-2 gap-4">
            <div className="card-earthy">
                <div className="flex items-center gap-2 mb-3">
                    <Shield size={18} className="text-brand" />
                    <h2 className="font-display text-lg font-medium text-brand">LGPD e limites de acesso</h2>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                    Como cuidador, você acessa somente os dados dos pacientes vinculados à sua conta pelo médico. Todos os seus acessos são registrados no sistema.
                </p>
                <ul className="text-sm space-y-2 mb-4">
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Ver plano de cuidado e sinais de alerta do paciente</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Visualizar medicamentos e horários</li>
                    <li className="flex gap-2"><span className="text-[var(--brand-secondary)]">✓</span>Registrar minha própria sobrecarga (Zarit)</li>
                    <li className="flex gap-2"><span className="text-muted-clinical">✕</span>CPF, número de documentos ou dados sensíveis nunca são exibidos</li>
                    <li className="flex gap-2"><span className="text-muted-clinical">✕</span>Alterar dados clínicos (apenas o médico pode)</li>
                </ul>
                <button data-testid="caregiver-portal-export-btn" onClick={exportData} className="btn-primary text-sm"><Download size={14} /> Baixar meus dados</button>
            </div>

            <div className="card-earthy">
                <div className="flex items-center gap-2 mb-3">
                    <Eye size={18} className="text-brand" />
                    <h2 className="font-display text-lg font-medium text-brand">Meus acessos recentes</h2>
                </div>
                {loading ? <p className="text-sm text-muted-clinical">Carregando…</p> : (
                    <div className="max-h-[400px] overflow-y-auto space-y-1.5 text-xs">
                        {logs.slice(0, 30).map((l) => (
                            <div key={l.id} className="p-2 rounded-lg bg-alt flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-brand">{l.action} · {l.target}</div>
                                </div>
                                <div className="text-muted-clinical">{new Date(l.created_at).toLocaleString("pt-BR")}</div>
                            </div>
                        ))}
                        {logs.length === 0 && <p className="text-muted-clinical">Nenhum acesso registrado ainda.</p>}
                    </div>
                )}
            </div>
        </div>
    );
}

function FormattedMarkdown({ text }) {
    return <SharedFormattedMarkdown text={text} />;
}
